import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

export default function SupportChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isWaitingForEmail, setIsWaitingForEmail] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [problemDescription, setProblemDescription] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addBotMessage('Hallo! Ich bin Ihr Support-Assistent. Wie kann ich Ihnen bei der Nutzung von HealthLeads helfen?');
    }
  }, [isOpen]);

  const addBotMessage = (text: string) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      text,
      sender: 'bot',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newMessage]);
  };

  const addUserMessage = (text: string) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      text,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newMessage]);
  };

  const sendSupportNotification = async (email: string, problem: string) => {
    try {
      setIsSending(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      const userName = user?.email?.split('@')[0] || 'Unbekannt';

      const { data, error } = await supabase.functions.invoke('send-support-notification', {
        body: {
          userName,
          userEmail: email,
          message: problem,
        },
      });

      if (error) {
        console.error('❌ Fehler beim Senden der Support-Benachrichtigung:', error);
        addBotMessage('Es gab einen Fehler beim Senden Ihrer Anfrage. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt unter olaf.niggemann@icloud.com');
        return false;
      }

      console.log('✅ Support-Benachrichtigung erfolgreich gesendet:', data);
      return true;
    } catch (error) {
      console.error('❌ Fehler:', error);
      addBotMessage('Es gab einen Fehler beim Senden Ihrer Anfrage. Bitte versuchen Sie es später erneut.');
      return false;
    } finally {
      setIsSending(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputText.trim() || isSending) return;

    const userInput = inputText.trim();
    addUserMessage(userInput);
    setInputText('');

    // Wenn wir auf E-Mail warten
    if (isWaitingForEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      
      if (emailRegex.test(userInput)) {
        setUserEmail(userInput);
        addBotMessage('Vielen Dank! Ich leite Ihr Problem jetzt an unser Support-Team weiter...');
        
        const success = await sendSupportNotification(userInput, problemDescription);
        
        if (success) {
          addBotMessage('✅ Ihr Problem wurde erfolgreich an unser Support-Team gemeldet. Sie erhalten in Kürze eine Antwort an Ihre E-Mail-Adresse. Gibt es noch etwas, bei dem ich Ihnen helfen kann?');
        }
        
        setIsWaitingForEmail(false);
        setProblemDescription('');
      } else {
        addBotMessage('Das scheint keine gültige E-Mail-Adresse zu sein. Bitte geben Sie eine gültige E-Mail-Adresse ein (z.B. name@beispiel.de)');
      }
      return;
    }

    // Prüfen ob es ein Problem ist
    const problemKeywords = ['funktioniert nicht', 'fehler', 'problem', 'bug', 'geht nicht', 'kaputt', 'hilfe', 'error', 'nicht möglich', 'kann nicht'];
    const isProblem = problemKeywords.some(keyword => userInput.toLowerCase().includes(keyword));

    if (isProblem) {
      setProblemDescription(userInput);
      setIsWaitingForEmail(true);
      addBotMessage('Das tut mir leid zu hören! Ich leite Ihr Problem gerne an unser Support-Team weiter. Können Sie mir bitte Ihre E-Mail-Adresse geben, damit wir Sie kontaktieren können?');
    } else {
      // Allgemeine Antworten
      if (userInput.toLowerCase().includes('nem')) {
        addBotMessage('Der NEM-Score (Network, Engagement, Market) bewertet die Relevanz eines Leads. Sie finden die NEM-Analyse im Dashboard unter "Lead Discovery". Haben Sie weitere Fragen?');
      } else if (userInput.toLowerCase().includes('lead')) {
        addBotMessage('Sie können neue Leads über "Lead Discovery" suchen und analysieren. Im CRM-Bereich können Sie Ihre Leads verwalten und kontaktieren. Benötigen Sie weitere Hilfe?');
      } else if (userInput.toLowerCase().includes('email') || userInput.toLowerCase().includes('e-mail')) {
        addBotMessage('Sie können E-Mails direkt aus dem CRM versenden. Klicken Sie auf einen Lead und wählen Sie "E-Mail senden". Es stehen auch KI-generierte Vorlagen zur Verfügung. Kann ich Ihnen noch bei etwas helfen?');
      } else {
        addBotMessage('Ich verstehe. Können Sie mir mehr Details geben? Oder beschreiben Sie mir Ihr Problem, damit ich es an unser Support-Team weiterleiten kann.');
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-teal-700 hover:bg-teal-800 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 z-50"
          aria-label="Support Chat öffnen"
        >
          <i className="ri-question-answer-line text-2xl"></i>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-lg shadow-2xl flex flex-col z-50 border border-gray-200">
          {/* Header */}
          <div className="bg-teal-700 text-white p-4 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-customer-service-2-line text-xl"></i>
              </div>
              <div>
                <h3 className="font-semibold">Support Chat</h3>
                <p className="text-xs text-teal-100">Wir helfen Ihnen gerne</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
              aria-label="Chat schließen"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.sender === 'user'
                      ? 'bg-teal-700 text-white'
                      : 'bg-white text-gray-800 border border-gray-200'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                  <p className={`text-xs mt-1 ${message.sender === 'user' ? 'text-teal-100' : 'text-gray-500'}`}>
                    {message.timestamp.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            {isSending && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-teal-700 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-teal-700 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-teal-700 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-lg">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={isWaitingForEmail ? 'Ihre E-Mail-Adresse...' : 'Ihre Nachricht...'}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                disabled={isSending}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputText.trim() || isSending}
                className="px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                <i className="ri-send-plane-fill"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
